import { SubscriptionStatus } from '@neogeniux/api-contracts';
import { useHasPermission } from '@app/shared/hooks/permission.hook';
import { useBusinessContextReady } from '@app/shared/hooks/use-business-context-ready';
import { selfBillingService } from '@app/shared/services/self-billing.service';
import { useAuthStore } from '@app/shared/store/auth.store';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const KEY = ['self-billing'] as const;

export const useMyBilling = () => {
  // `billing.self.manage` gates the /billing/me endpoint server-side
  // (only admin / super_admin carry it). Without the FE gate every
  // non-admin login fires the request and eats a 403 — surfaces
  // like the team-switcher plan label that depend on this hook
  // already tolerate `undefined`.
  const canRead = useHasPermission('billing.self.manage');
  // Auth-swap window: without this gate the hook fires with an empty
  // branch header on impersonation, then refires when the branch
  // store settles. See use-business-context-ready.ts.
  const contextReady = useBusinessContextReady();
  // Scope cache by businessId so sadmin's own billing cache cannot
  // paint into an impersonated business (see profile.hook fix for the
  // same reasoning).
  const businessId = useAuthStore((s) => s.user?.businessId);
  return useQuery({
    queryKey: [...KEY, 'me', businessId ?? 'anon'],
    queryFn: () => selfBillingService.getMine(),
    enabled: canRead && contextReady,
    // Poll every 5s while the tenant is in a recovery state so the
    // suspension banner + gated sections auto-clear the moment the
    // backend reactivateSubscriptionAfterPayment fires — no manual
    // refresh. Card-save wake-up is fire-and-forget on the backend
    // (`materializeAndAdvance.then(processInvoice)`), so the exact
    // moment of reactivation can trail the mutation's onSuccess by
    // a few seconds. Stops polling when status flips to
    // active/trialing/cancelled so healthy tenants pay zero cost.
    refetchInterval: (query) => {
      const status = query.state.data?.subscriptionStatus;
      return status === SubscriptionStatus.PAST_DUE ||
        status === SubscriptionStatus.SUSPENDED
        ? 5000
        : false;
    },
  });
};

export const useMyPayments = () => {
  // Same gate as `useMyBilling` — GET /billing/me/payments is
  // `billing.self.manage`-gated server-side; don't fire for a role
  // without it (cashier / vendor) or it just 403s.
  const canRead = useHasPermission('billing.self.manage');
  return useQuery({
    queryKey: [...KEY, 'payments'],
    queryFn: () => selfBillingService.listPayments(),
    select: (data) => (Array.isArray(data) ? data : []),
    enabled: canRead,
  });
};

export const useSelfCheckout = () =>
  useMutation({
    mutationFn: selfBillingService.checkout,
  });

/**
 * Preview endpoint powering the "Confirmar cambio de plan" modal.
 * Returns proration numbers (unused credit + prorated charge + carry
 * credit for downgrades) and the list of cortesía grants that will
 * be revoked when the swap fires. Only enabled when a plan code is
 * supplied so the modal doesn't fire an idle query on the plan grid.
 */
export const useMyPlanChangePreview = (planTemplateCode: string | null) => {
  const canRead = useHasPermission('billing.self.manage');
  return useQuery({
    queryKey: ['self-billing', 'plan-change-preview', planTemplateCode],
    queryFn: () =>
      selfBillingService.previewPlanChange(planTemplateCode as string),
    enabled: canRead && !!planTemplateCode,
    staleTime: 15 * 1000,
  });
};

export const useSelfChangePlan = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (planTemplateCode: string) =>
      selfBillingService.changePlan(planTemplateCode),
    onSuccess: async () => {
      await Promise.all([
        // Force the /billing/me query to refetch immediately, not
        // just mark it stale. Plan changes flip the "Plan actual"
        // badge — leaving it on the previous code creates a UI/state
        // mismatch the user notices the second the toast lands.
        qc.refetchQueries({ queryKey: KEY }),
        qc.invalidateQueries({
          queryKey: ['subscription', 'me', 'items'],
        }),
        qc.invalidateQueries({ queryKey: ['sellable-catalog'] }),
        qc.invalidateQueries({ queryKey: ['subscription-changes', 'me'] }),
        // Next-invoice preview reads active `subscription_override`
        // rows — the backend now revokes every override on plan
        // swap, but /account "Próximo cobro" + payments-history
        // synthetic next-payment row still render the stale
        // "Cortesía RD$720 → RD$0" line until this cache turns over.
        // Refetch (not just invalidate) so the render is fresh by
        // the time the plan-change toast lands.
        qc.refetchQueries({ queryKey: ['my-next-invoice-preview'] }),
        qc.invalidateQueries({ queryKey: ['self-billing', 'payments'] }),
        // /account plan-usage capacity banner + any surface driven
        // by active overrides.
        qc.invalidateQueries({
          queryKey: ['subscription', 'me', 'plan-usage'],
        }),
        // Plan upgrade applies immediately on the backend; plan
        // downgrade reroutes through scheduler. Either way menu /
        // permission gates shift, so flip every cached query whose
        // key signals plan-derived state.
        qc.invalidateQueries({
          predicate: (q) => {
            const key = q.queryKey as readonly unknown[];
            if (!Array.isArray(key) || key.length === 0) return false;
            const first = String(key[0]);
            return (
              first === 'menu' ||
              first.includes('permission') ||
              first.includes('capability') ||
              first.includes('role') ||
              // Sadmin + operator override / grant panels.
              first.includes('grant') ||
              first.includes('override')
            );
          },
        }),
      ]);
    },
  });
};

export const useSelfCancel = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (atPeriodEnd: boolean) =>
      selfBillingService.cancel(atPeriodEnd),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
};

export const useSelfResume = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => selfBillingService.resume(),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
};

export const useSetBillingInterval = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (interval: 'monthly' | 'annual') =>
      selfBillingService.setBillingInterval(interval),
    onSuccess: async () => {
      // Force-refetch every surface whose contents change with the
      // interval flip — the borrador row in Historial de pagos
      // (customer-billing/invoices), the "Próximo cobro" synthetic
      // row + payments paginator (subscription/me + subscription/me/items
      // + subscription/me/addons), and the timeline event that
      // BillingService.updateBillingInterval just recorded.
      await Promise.all([
        qc.refetchQueries({ queryKey: KEY }),
        qc.refetchQueries({ queryKey: ['customer-billing'] }),
        qc.refetchQueries({ queryKey: ['subscription', 'me'] }),
        qc.refetchQueries({ queryKey: ['subscription-timeline'] }),
      ]);
    },
  });
};
